import { useState, useEffect, useCallback } from 'react';
import { useField } from 'formik';
import { InputGroup, Box, Flex } from '@chakra-ui/react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { editMe } from '@/store/me/actions';
import { selectUser } from '@/store/bootstrap/selectors';
import { TimeZoneSelectorV1 } from './components/TimeZoneSelectorV1';
import { CustomTimeSelectorTz } from './components/CustomTimeSelectorTz';
import { DateSinglePicker } from '@/design-system/date-picker';
import { selectMeForUpdate } from '@/store/me';
import { getDefaultTimeZone } from './helpers';
import { KText } from '@/ui-library/components';

export type TimeZoneOption = { label: string; key: string };

type DateWrapper = {
  formattedTime?: string;
  time?: string;
  timezoneValue?: TimeZoneOption;
  isFutureDate?: boolean;
};

export const DateFieldWithTZ = ({ item }) => {
  const {
    name,
    className,
    enableTime = false,
    disablePreviousDate = false,
    customOnChange = () => {},
    showError = true,
  } = item || {};
  const { time_zones } = (useSelector(selectMeForUpdate) || {}) as {
    time_zones?: Record<string, string>;
  };

  const currUser = useSelector(selectUser);
  //To get the default user Time-format(12hr or 24 hr) and date-format
  const { time_fmt, time_zone } = currUser || {};

  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState<string | undefined>();
  const [selectedTimezone, setSelectedTimezone] = useState<TimeZoneOption>(() =>
    getDefaultTimeZone(time_zone)
  );
  const [dateWrapper, setDateWrapper] = useState<DateWrapper>({});

  // To set the meta values(edit-values) using formik helpers
  const [, meta, helpers] = useField(name);

  //setValue to return the selected values to component
  const { setValue, setTouched } = helpers || {};

  const { value } = meta || {};

  const getTimeFormat = () => {
    return time_fmt === 'hour_12' ? 'hh:mm A' : 'HH:mm';
  };

  // variable store selected-timezone date(starting date in that selected timezone)

  const dispatch = useDispatch();

  useEffect(() => {
    if (enableTime) {
      dispatch(editMe({}));
    }
  }, [dispatch, enableTime]);

  // Function to check if a date is the current day in the selected timezone
  const isCurrentDay = useCallback(
    (date) => {
      if (!date || !selectedTimezone) return false;

      const currentDateInTZ = moment().tz(selectedTimezone.key);
      const dateToCheck = moment(date).tz(selectedTimezone.key, true);

      return (
        dateToCheck.format('YYYY-MM-DD') ===
        currentDateInTZ.format('YYYY-MM-DD')
      );
    },
    [selectedTimezone]
  );

  // set selected date, time and timeZone values
  const handleDateChange = (date) => {
    setSelectedDate(date);

    // If selected date is current day in the selected timezone and no time selected yet,
    // update time to current time plus buffer
    if (date && selectedTimezone && isCurrentDay(date) && !selectedTime) {
      const nowInSelectedTimezone = moment().tz(selectedTimezone.key);
      const roundedMinutes =
        Math.ceil(nowInSelectedTimezone.minutes() / 15) * 15;
      nowInSelectedTimezone.minutes(roundedMinutes).seconds(0);
      setSelectedTime(nowInSelectedTimezone.format('HH:mm'));
    }
  };
  // Function to handle time change
  const handleTimeChange = (time) => {
    setSelectedTime(time);
  };
  //Function-handles-time-zone-change
  const handleTimeZoneChange = (zone) => {
    setSelectedTimezone(zone);

    if (!selectedDate) return;

    const nowInNewTimezone = moment().tz(zone.key);

    // Check if selected date is in the past in the new timezone
    const selectedDateInNewTZ = moment(selectedDate).tz(zone.key, true);

    // If selected date is in the past in new timezone, update it to current date
    if (
      selectedDateInNewTZ.startOf('day').valueOf() <
      nowInNewTimezone.startOf('day').valueOf()
    ) {
      setSelectedDate(nowInNewTimezone.toDate());
      // Only reset time if date was changed to current date and no time was selected
      if (!selectedTime) {
        const roundedMinutes = Math.ceil(nowInNewTimezone.minutes() / 15) * 15;
        nowInNewTimezone.minutes(roundedMinutes).seconds(0);
        setSelectedTime(nowInNewTimezone.format('HH:mm'));
      }
    }
  };

  const setFinalFormattedDate = () => {
    let updatedDate = moment(selectedDate);

    if (selectedTimezone !== null && Object.keys(selectedTimezone)) {
      updatedDate = updatedDate.tz(selectedTimezone.key, true);
    }

    if (selectedTime) {
      const selectedTimeSplited = selectedTime.split(':');
      updatedDate.set({
        hour: Number(selectedTimeSplited[0]),
        minute: Number(selectedTimeSplited[1]),
      });
    }
    const dateWrapperItems = {
      formattedTime: updatedDate.format('YYYY-MM-DDTHH:mm:ssZ'),
      time: selectedTime,
      timezoneValue: selectedTimezone,
      isFutureDate: hasFutureDate(),
    };
    setDateWrapper(dateWrapperItems);
    setValue(dateWrapperItems);
    setTouched(true);
    customOnChange(dateWrapperItems);
  };

  const hasFutureDate = () => {
    if (!selectedDate || !selectedTime || !selectedTimezone) return false;

    const currentTimeInSelectedTZ = moment().tz(selectedTimezone.key);

    // Parse selected time
    const [hour, minute] = (selectedTime ?? '').split(':').map(Number);

    // Create a moment with the selected date and time in the selected timezone
    const selectedMoment = moment(selectedDate)
      .tz(selectedTimezone.key, true)
      .set({
        hour,
        minute,
        second: 0,
      });

    // Check if the selected date and time are in the future
    return selectedMoment.isAfter(currentTimeInSelectedTZ);
  };

  // useEffect to update form values when any dependency changes
  /* eslint-disable react-hooks/set-state-in-effect -- pre-existing mount-time
     prop -> state sync, surfaced by the .jsx to .tsx rename. */
  useEffect(() => {
    if (selectedDate && selectedTime && selectedTimezone) {
      setFinalFormattedDate();
    }
    // eslint-disable-next-line
  }, [selectedDate, selectedTime, selectedTimezone]);

  // Additional useEffect to ensure time is valid when timezone changes
  // Only update time if no time is selected yet, don't override user's selection
  useEffect(() => {
    if (
      selectedTimezone &&
      selectedDate &&
      isCurrentDay(selectedDate) &&
      !selectedTime
    ) {
      // Get current time in new timezone
      const nowInSelectedTZ = moment().tz(selectedTimezone.key);
      const roundedMinutes = Math.ceil(nowInSelectedTZ.minutes() / 15) * 15;
      nowInSelectedTZ.minutes(roundedMinutes).seconds(0);

      // Update time if needed
      const newTime = nowInSelectedTZ.format('HH:mm');
      setSelectedTime(newTime);
    }
  }, [isCurrentDay, selectedDate, selectedTimezone, selectedTime]);

  // function to disable the past days in the caleander
  const disablePreviousDates = (date) => {
    if (!selectedTimezone || !selectedTimezone.key) return false;

    // Get current date in the selected timezone
    const currentDateInSelectedTZ = moment().tz(selectedTimezone.key);

    // Convert the date to be checked to the selected timezone
    const dateToCheck = moment(date).tz(selectedTimezone.key, true);

    // Compare dates after setting both to start of day in the selected timezone
    return (
      dateToCheck.startOf('day').valueOf() <
      currentDateInSelectedTZ.startOf('day').valueOf()
    );
  };

  const getCurrentTimePlus30 = (time = null) => {
    const now = moment().tz(currUser?.time_zone || moment.tz.guess());

    if (time) {
      // which means data is coming from server
      const updatedT = moment.parseZone(time).format('HH:mm');
      return updatedT;
    }
    const roundedMinutes = Math.ceil(now.minutes() / 30) * 30;
    // Set the rounded minutes and reset seconds to 0
    now.minutes(roundedMinutes).seconds(0);
    // Format in 24-hour format (HH:mm)
    return now.format('HH:mm');
  };

  // Function to filter time options to remove past times if current day is selected
  const filterPastTimeOptions = (timeOptions) => {
    // If not current day or no timezone selected, return all options
    if (!isCurrentDay(selectedDate) || !selectedTimezone) {
      return timeOptions;
    }

    const currentTime = moment().tz(selectedTimezone.key);
    const currentHour = currentTime.hour();
    const currentMinute = currentTime.minute();

    // Filter out time options that are in the past
    return timeOptions.filter((option) => {
      const [hours, minutes] = option.value.split(':').map(Number);
      if (hours < currentHour) return false;
      if (hours === currentHour && minutes <= currentMinute) return false;
      return true;
    });
  };

  useEffect(() => {
    if (!time_zones) return;
    if (meta?.value === undefined) {
      // default case
      setSelectedDate(moment().toDate());
      setSelectedTime(getCurrentTimePlus30());
    } else {
      const { formattedTime } = meta?.value as { formattedTime?: string };
      let { time, timezoneValue: timezone } = meta?.value as {
        formattedTime?: string;
        time?: string;
        timezoneValue: TimeZoneOption | string;
      };
      if (typeof timezone === 'string') {
        // which means data is coming from server
        const objectKeys = Object.keys(time_zones);
        objectKeys.some((itr) => {
          if (itr === timezone) {
            timezone = {
              key: itr,
              label: `(GMT${time_zones[itr]} ${itr})`,
            };
            return true;
          }
          return false;
        });
        time = getCurrentTimePlus30(formattedTime);

        setSelectedDate(
          moment(moment.parseZone(formattedTime).format('MM-DD-YYYY')).toDate()
        );
        if (typeof timezone !== 'string') setSelectedTimezone(timezone);
        setSelectedTime(time);
      } else {
        setSelectedDate(moment(formattedTime).toDate());
        if (typeof timezone !== 'string') setSelectedTimezone(timezone);
        setSelectedTime(time);
      }
    }
    // eslint-disable-next-line
  }, [time_zones]);

  return (
    <Box>
      <div className="custom-ats-datepicker-wrapper date-field-with-timezone-wrapper">
        <InputGroup>
          <DateSinglePicker
            isStatic
            value={selectedDate}
            onChange={handleDateChange}
            // DateFieldWithTZ has always drawn a Monday-first grid.
            weekStartsOn={1}
            minDate={disablePreviousDate ? new Date() : undefined}
            // Past days are judged in the selected timezone, not the browser's.
            disabledDay={disablePreviousDate ? disablePreviousDates : undefined}
            calendarClassName={className || undefined}
          />
        </InputGroup>
      </div>
      {enableTime && (
        <Flex flexDir="column" gap="6px">
          <CustomTimeSelectorTz
            date={selectedDate}
            value={selectedTime}
            onChange={handleTimeChange}
            timeFormat={getTimeFormat()}
            metaData={value}
            isCurrentDay={isCurrentDay(selectedDate)}
            filterTimeOptions={filterPastTimeOptions}
            timeZone={selectedTimezone}
          />
          <TimeZoneSelectorV1
            value={selectedTimezone}
            onChange={handleTimeZoneChange}
          />
          {!dateWrapper?.isFutureDate && showError && (
            <KText
              pt="sm"
              color={'pastel-1-dark'}
              size="sm-medium"
              position={'relative'}
            >
              Please select future date and time
            </KText>
          )}
        </Flex>
      )}
    </Box>
  );
};
